/**
 * Slug-keyed value-option overrides for the QueryBuilder.
 *
 * When a `custom:<slug>` field appears in this map, the QueryBuilder
 * renders a dropdown (for equals / not_equals) or a checkbox group
 * (for in / not_in) sourced from the configured options, instead of
 * the default free-text input.
 *
 * Custom attributes NOT in this map continue to render as free text,
 * preserving existing behavior. Add entries only when the value space
 * is small, stable, and finite — otherwise prefer a server-side RPC
 * or leave as free text.
 *
 * Each entry MUST include the verification date and the SQL used to
 * derive the option list. This is the only audit trail that explains
 * why a particular set of strings was chosen.
 */

export interface CustomAttributeOptionList {
  /** Display label shown above the picker. Defaults to the QueryField label otherwise. */
  label?: string;
  /** Allowed values. Stored verbatim in criteria value_text. */
  values: string[];
}

/**
 * Verified 2026-05-26 against QA (`qqynnldqhtstlfiimnwm`) and prod
 * (`nnzheppmjkoygpdbrqkj`). Source query:
 *
 *   SELECT trim(v.value_text) AS value, count(*)
 *   FROM contact_attribute_values v
 *   JOIN contact_attribute_definitions d ON d.id = v.attribute_id
 *   WHERE d.slug = '<slug>'
 *     AND v.value_text IS NOT NULL
 *     AND trim(v.value_text) <> ''
 *   GROUP BY trim(v.value_text)
 *   ORDER BY count DESC, value ASC;
 *
 * Re-run periodically if Pardot adds a new product tier and the
 * dropdown starts dropping values.
 */
export const CUSTOM_ATTRIBUTE_VALUE_OVERRIDES: Record<
  string,
  CustomAttributeOptionList
> = {
  pardot_account_subscription_tier: {
    label: "Subscription Tier",
    // USLS product-tier vocabulary. Identical in QA and prod on the
    // verification date. New tiers would be a deliberate product
    // launch, not data drift, so a static list is acceptable here.
    values: ["Defend", "Discover", "Explore", "Navigate"],
  },
};

/**
 * Extract the slug from a `custom:<slug>` QueryField string, or null if
 * the input does not match the custom-attribute prefix.
 */
export function getCustomAttrSlug(field: string): string | null {
  if (typeof field !== "string" || !field.startsWith("custom:")) {
    return null;
  }
  return field.slice("custom:".length);
}

/**
 * Look up the configured override for a `custom:<slug>` field. Returns
 * null when the field is not a custom attribute, or when no override
 * exists for that slug. Callers should fall through to the default
 * free-text rendering when this returns null.
 */
export function getCustomAttributeOverride(
  field: string,
): CustomAttributeOptionList | null {
  const slug = getCustomAttrSlug(field);
  if (!slug) {
    return null;
  }
  return CUSTOM_ATTRIBUTE_VALUE_OVERRIDES[slug] ?? null;
}
